import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import * as _ from 'lodash';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, startWith, distinctUntilChanged, tap } from 'rxjs/operators';
import { ShippingOrder, ShippingOrderExtended, Site } from 'src/app/models';
import { ShippingOrdersService } from 'src/app/services/shipping-orders.service';
import { UsersService } from 'src/app/services/users.service';
import { FormGroup, FormBuilder } from '@angular/forms';
import { SitesService } from 'src/app/services/sites.service';

@Component({
  selector: 'app-shipping-orders',
  templateUrl: './shipping-orders.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./shipping-orders.component.scss'],
})
export class ShippingOrdersComponent implements OnInit {
  public currentUserCanEdit$: Observable<boolean>;
  public currentUserIsAdmin$: Observable<boolean>;
  public filterValue$: Observable<string>;
  public form: FormGroup;
  public gridData$: Observable<GridDataResult>;
  public loaded$: Observable<boolean>;
  public pageSize = 40;
  public rowHeight = 30;
  public skip$ = new BehaviorSubject<number>(0);
  public userSites$: Observable<Site[]>;
  public sort$ = new BehaviorSubject<SortDescriptor[]>([
    {
      field: 'number',
      dir: 'asc',
    },
  ]);

  constructor(
    public dialog: MatDialog,
    public shippingOrdersService: ShippingOrdersService,
    private userService: UsersService,
    private sitesService: SitesService,
    private fb: FormBuilder,
  ) {}

  ngOnInit() {
    this.loaded$ = this.shippingOrdersService.loaded$;
    this.filterValue$ = this.shippingOrdersService.filter$;
    this.currentUserCanEdit$ = this.userService.currentUserCanEdit$;
    this.currentUserIsAdmin$ = this.userService.currentUserIsAdmin$;
    this.shippingOrdersService.getAll();
    this.userSites$ = this.sitesService.userSites$;

    this.form = this.fb.group({
      siteFilter: [null],
    });

    const siteFilterChanged$: Observable<number> = this.form.get('siteFilter').valueChanges.pipe(
      startWith(null),
      distinctUntilChanged(),
      tap(() => {
        // Reset page to top
        this.skip$.next(0);
      }),
    );

    this.gridData$ = combineLatest(
      this.shippingOrdersService.filteredEntities$,
      siteFilterChanged$,
      this.sort$,
      this.skip$,
    ).pipe(
      map(([shippingOrders, siteFilter, [sort], skip]) => {
        const data =
          sort.dir === 'asc' ? _.sortBy(shippingOrders, sort.field) : _.sortBy(shippingOrders, sort.field).reverse();

        const filteredData = data.filter((shippingOrder) => {
          if (siteFilter) {
            return shippingOrder.siteId === siteFilter;
          } else {
            return true;
          }
        });

        const pagedData = filteredData.slice(skip, skip + this.pageSize);
        return { data: pagedData, total: filteredData.length };
      }),
    );
  }
  openNewDialog() {
    this.shippingOrdersService.openNewDialog().subscribe();
  }
  openEditDialog(shippingOrder: ShippingOrderExtended) {
    this.shippingOrdersService.openEditDialog(shippingOrder).subscribe();
  }
  delete(shippingOrder: ShippingOrderExtended) {
    this.shippingOrdersService.delete(shippingOrder);
  }
  filterShippingOrders(filter) {
    this.shippingOrdersService.setFilter(filter);
  }
  public sortChange(sort: SortDescriptor[]) {
    this.sort$.next(sort);
  }

  public pageChange(event: PageChangeEvent) {
    this.skip$.next(event.skip);
  }
}
